import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import SearchBox from "components/__new/common/SearchBox";
import Table from "components/__new/common/Table";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import { TableContent } from "components/__new/common/TableContent";
import DateFormat, { MonthName, TimeFormat } from "utils/dateFormat";
import CurrencyFormat from "utils/currencyFormat";
import Pagination from "components/__new/common/pagination";
import Maybe from "components/__new/common/Maybe";
import processParams from "utils/processParams";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { renderStatusColor } from "utils/statusColor";
import { toast } from "react-toastify";
import { BackButtonPage } from "components/__new/common/BackButton";
import { ReactComponent as DownloadIcon } from "assets/images/icons/project-icons/download.svg";
import { ReactComponent as CalendarIcon } from "assets/images/icons/calendar.svg";
import PageContent from "components/__new/common/PageContent";
import iconHome from "assets/images/icons/icon-home.svg";
import iconUser from "assets/images/icons/icon-user.svg";
import iconDoc from "assets/images/icons/icon-doc.svg";
import { ReactComponent as ImgCopy } from "assets/images/icons/copy.svg";
import copy from "copy-to-clipboard";
import DownloadAsCSV from "components/__new/common/DownloadAsCSV";
import Print from "components/__new/common/Print";
import useLoginInfo from "hooks/useLoginInfo";
import { addOtherWithdrawalFilters } from "pages/withdrawals/list";
import * as paymentNoteAction from "store/entities/invoice/action";
import * as withdrawalsAction from "store/entities/withdrawals/action";
import * as walletAction from "store/entities/wallet/action";

export const Details = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    const { invoiceId } = useParams();
    const {urlQueryParams} = useUrlQueryParams();
    const { paymentNoteLoading, paymentNote, invoicePDFLoading, invoicePDF } = useSelector((s) => s.entities.invoice);
    const { withdrawalsLoading, withdrawals, withdrawalsDownloadLoading, withdrawalsDownload } = useSelector((s) => s.entities.withdrawals);
    const { dashboardLoading: walletInfoLoading, dashboard: walletInfo } = useSelector((s) => s.entities.wallet);

    
    const downloadAsCSVRef = useRef();
    const printComponentRef = useRef();
    const [downloadInvoicePDFInitiated, setDownloadInvoicePDFInitiated] = useState(false);
    const [month, setMonth] = useState("");
    const [year, setYear] = useState("");
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });


    const getPaymentNote = () => {
        dispatch(paymentNoteAction.getInvoice(invoiceId));
    }


    const getWalletInfo = () => {
        if (walletInfoLoading === false && walletInfo?.status !== "success"){
            dispatch(walletAction.getDashboardSilent(userInfo.token));
        }
    }


    const getWithdrawals = () => {
        let params = {...listParams};

        const paymentNoteMonth = MonthName(paymentNote?.invoice?.month);
        const paymentNoteYear = paymentNote?.invoice?.year;
        
        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "status", "string", "", "status");
        params = processParams(urlQueryParams, params, "search", "string", "", "search");
        params = processParams(urlQueryParams, params, "month", "string", paymentNoteMonth, "month");
        params = processParams(urlQueryParams, params, "year", "string", paymentNoteYear, "year");
        
        setMonth(paymentNoteMonth);
        setYear(paymentNoteYear);
        setListParams(params);

        const paramsPayload = addOtherWithdrawalFilters(params);
        dispatch(withdrawalsAction.getWithdrawals(paramsPayload));
    }
    

    const downloadInvoicePDF = () => {
        const downloadInvoicePDFParams = {
            paymentId: paymentNote?.invoice?.invoiceId,
        }
        setDownloadInvoicePDFInitiated(true);
        dispatch(paymentNoteAction.downloadInvoicePDF(downloadInvoicePDFParams));
        toast("Downloading PDF");
    }


    useEffect(() => {
        if (invoiceId){
            getPaymentNote();
            getWalletInfo();
        }
        // eslint-disable-next-line
    }, [invoiceId]);


    useEffect(() => {
        if (invoiceId && paymentNoteLoading === false && paymentNote?.status === "success" && paymentNote?.invoice !== null){
            getWithdrawals();
        }
        // eslint-disable-next-line
    }, [location, paymentNoteLoading, paymentNote]);


    useEffect(() => {
        if (downloadInvoicePDFInitiated === true){
            if (invoicePDFLoading === false){
                if (invoicePDF?.status === "success"){
                    const linkSource = `data:application/pdf;base64,${invoicePDF?.pdf}`;
                    const downloadLink = document.createElement("a");
                    const fileName = `Payment Note (${month} ${year}).pdf`;
                    downloadLink.href = linkSource;
                    downloadLink.download = fileName;
                    downloadLink.click();
                    toast.success("PDF downloaded");
                }
                else{
                    toast.error("An error occurred while downloading payment note.");
                }

                setDownloadInvoicePDFInitiated(false);
            }
        }
        // eslint-disable-next-line
    }, [invoicePDFLoading, invoicePDF]);


    return (
        <div>

            <DynamicHeadTag
                headerText="Payment note"
                headerDescription="See all pending payment transactions for easy tracking and record-keeping purposes."
            />


            <div className="lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title flex">
                        <BackButtonPage />
                        Payment Summary
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3">
                    <Maybe condition={paymentNoteLoading === false && paymentNote?.status === "success"}>
                        <div onClick={downloadInvoicePDF} className="h-10 btn px-3 border border-gray-300 flex cursor-pointer">
                            {invoicePDFLoading && <FontAwesomeIcon icon="circle-notch" spin className="my-auto" /> }
                            {!invoicePDFLoading && <DownloadIcon className="customizeSVG my-auto" /> }
                            <span>Download Payment Note</span>
                        </div>
                    </Maybe>
                </div>
            </div>


            <div className="mt-6 space-y-6 xl:space-y-0 xl:grid xl:grid-cols-5 xl:gap-x-6">
                <div className="box xl:col-span-3">
                    <PageContent
                        loading={paymentNoteLoading}
                        data={paymentNote}
                        loadingText="Loading payment note"
                        emptyText="Payment note not found"
                        errorText="Unable to load payment note"
                    />

                    <div className="flex">
                        <Maybe condition={paymentNoteLoading === false && paymentNote?.status === "success"}>
                            <div className="w-full my-auto flex space-x-4 justify-between">
                                <div className="my-auto">
                                    <div className="font-medium">
                                        {paymentNote?.invoice?.invoiceId}
                                    </div>
                                    <div className="mt-6">
                                        <div className="text-3xl md:text-4xl font-bold font-dm-sans">
                                            {/* {CurrencyFormat(paymentNote?.invoice?.amount)} */}
                                            {CurrencyFormat((paymentNote?.invoice?.amount || 0) - (paymentNote?.invoice?.amountPaid || 0))}
                                        </div>
                                        <div className="mt-1 text-[#505666]">
                                            Amount Pending
                                        </div>
                                    </div>
                                    <div className="mt-6">
                                        <div className="flex">
                                            <CalendarIcon className="mr-2" />
                                            <span className="text-[#505666]">
                                                {MonthName(paymentNote?.invoice?.month)} {paymentNote?.invoice?.year}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-shrink-0 my-auto text-right">
                                    <div>
                                        <div className="font-bold font-dm-sans">
                                            {CurrencyFormat(paymentNote?.invoice?.amount)}
                                        </div>
                                        <div className="text-[#505666]">
                                            Total Amount
                                        </div>
                                    </div>
                                    <div className="mt-6">
                                        <div className="font-bold font-dm-sans">
                                            {CurrencyFormat(paymentNote?.invoice?.amountPaid)}
                                        </div>
                                        <div className="text-[#505666]">
                                            Amount Paid
                                        </div>
                                    </div>
                                    <div className="mt-6">
                                        <span className="text-[#505666]">
                                            Due Date:&nbsp;
                                        </span>
                                        <span className="font-medium">
                                            {DateFormat(paymentNote?.invoice?.dueDate)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Maybe>
                    </div>
                </div>

                <div className="box xl:col-span-2">
                    <div>
                        Payment Instructions
                    </div>
                    <div className="mt-1 font-medium">
                        Kindly make transfer into the account below
                    </div>

                    <PageContent
                        loading={walletInfoLoading}
                        data={walletInfo}
                        padding="lg:py-0"
                        loadingText="Loading Account Information"
                        emptyText="Account information not found"
                        errorText="Unable to load account information"
                    />

                    <Maybe condition={walletInfoLoading === false && walletInfo?.status === "success"}>
                        <div className="mt-6 space-y-3">
                            <AccountInfo
                                loading={false}
                                icon={iconHome}
                                name="Bank name"
                                value={walletInfo?.data?.walletDetails?.bankName || "-"}
                            />
                            <AccountInfo
                                loading={false}
                                icon={iconUser}
                                name="Account name"
                                value={walletInfo?.data?.walletDetails?.accountName || "-"}
                            />
                            <AccountInfo
                                loading={false}
                                icon={iconDoc}
                                copyToClipboard={true}
                                name="Account number"
                                value={walletInfo?.data?.walletDetails?.accountNumber || "-"}
                            />
                        </div>
                    </Maybe>
                </div>
            </div>

        
            <div className="mt-6 box !px-0 !pb-0">
                <div className="px-4 font-medium text-xl">
                    Withdrawals
                </div>

                <div className="px-4 mt-6 flex flex-wrap justify-between">
                    <div className="max-w-sm flex-grow sm:mr-6">
                        <SearchBox placeholder="Search by name, amount..." name="search" extraUrlParams="page=1" />
                    </div>
                    <div className="flex flex-wrap space-x-3 sm:space-x-6">
                        <div>
                            <DownloadAsCSV
                                ref={downloadAsCSVRef}
                                tooltip="Download withdrawals list"
                                filename="Withdrawals.csv"
                                columns={[
                                    { label: "Date Withdrawn", key: "date" },
                                    { label: "Full Name", key: "employeeId.fullName" },
                                    { label: "Job Title", key: "employeeId.jobTitle" },
                                    { label: "Email Address", key: "employeeId.email" },
                                    { label: "Phone Number", key: "employeeId.phoneNumber" },
                                    { label: "Amount Aggregated", key: "payBack" },
                                    { label: "Amount Requested", key: "amountRequested" },
                                    { label: "Actual Paid", key: "actualPaid" },
                                    { label: "Fee", key: "fee" },
                                    { label: "Status", key: "status" },
                                ]}
                                click={() => {
                                    dispatch(withdrawalsAction.downloadWithdrawals(addOtherWithdrawalFilters({...listParams, page: 1, limit: 10000}))).then(() => {
                                        downloadAsCSVRef.current?.downloadData();
                                    });
                                }}
                                loading={withdrawalsDownloadLoading}
                                response={withdrawalsDownload?.data?.withdrawals}
                            />
                        </div>

                        <div>
                            <Print component={printComponentRef} />
                        </div>
                    </div>
                </div>
                
                <div className="mt-6">
                    <div ref={printComponentRef}>

                        <div className="mb-4 text-xl hidden print:block">
                            Withdrawals
                        </div>
                        
                        <Table>
                            <Table.Head>
                                <th>Date Withdrawn</th>
                                <th>Employee Info</th>
                                <th>Net Salary</th>
                                <th>Amt Aggregated</th>
                                <th>Amt Requested</th>
                                <th>Actual Paid</th>
                                <th>Fee</th>
                                <th>Status</th>
                                <th></th>
                            </Table.Head>
                            <Table.Body>

                                <Maybe condition={paymentNoteLoading === true}>
                                    <tr className="table-no-data">
                                        <td colSpan="9">
                                            <PageContent loading={true} loadingText="Loading payment note" />
                                        </td>
                                    </tr>
                                </Maybe>

                                <Maybe condition={paymentNoteLoading === false}>
                                    <TableContent
                                        loading={withdrawalsLoading}
                                        loadingText="Loading withdrawals"
                                        data={withdrawals?.data?.withdrawals}
                                        total={withdrawals?.data?.total}
                                        colspan={9}
                                    />

                                    {withdrawalsLoading === false && withdrawals?.data?.withdrawals?.map((withdrawal) =>
                                        <tr key={withdrawal._id}>
                                            <td>
                                                <div>
                                                    {DateFormat(withdrawal.date)}
                                                </div>
                                                <div>
                                                    {TimeFormat(withdrawal.date)}
                                                </div>
                                            </td>
                                            <td>
                                                {(withdrawal.employeeId) &&
                                                    <>
                                                        <div>
                                                            {withdrawal.employeeId.fullName}
                                                        </div>
                                                        <div className="text-fade">
                                                            {withdrawal.employeeId.email}
                                                        </div>
                                                    </>
                                                }
                                            </td>
                                            <td>
                                                {CurrencyFormat(withdrawal.netSalary)}
                                            </td>
                                            <td>
                                                {CurrencyFormat(withdrawal.payBack)}
                                            </td>
                                            <td>
                                                {CurrencyFormat(withdrawal.amountRequested)}
                                            </td>
                                            <td>
                                                {CurrencyFormat(withdrawal.actualPaid)}
                                            </td>
                                            <td>
                                                <div>
                                                    {CurrencyFormat(withdrawal.fee)}
                                                </div>
                                                <div className="text-xs">
                                                    {withdrawal.feePayer === "shared" &&
                                                        <div>
                                                            Shared
                                                        </div>
                                                    }
                                                    {withdrawal.feePayer === "employee" &&
                                                        <div>
                                                            By Employee
                                                        </div>
                                                    }
                                                    {withdrawal.feePayer === "employer" &&
                                                        <div>
                                                            By Business
                                                        </div>
                                                    }
                                                </div>
                                            </td>
                                            <td>
                                                <div className={"label " + renderStatusColor(withdrawal.status)}>
                                                    {withdrawal.status}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Maybe>

                            </Table.Body>
                        </Table>

                        <Maybe condition={withdrawalsLoading === false && withdrawals?.data?.withdrawals?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={withdrawals?.data} limit={listParams.limit} />
                            </div>
                        </Maybe>

                    </div>
                </div>
            </div>

        </div>
    )
}



export const AccountInfo = (props) => {

    const copyValue = () => {
        copy(props.value);
        toast.success("Copied: " + props.value);
    }

    return (
        <div className="flex">
            <div className="w-44 mr-2 flex space-x-2">
                <img src={props.icon} alt={props.name} className="w-6 h-6 object-contain" />
                <div>
                    {props.name}
                </div>
            </div>
            <div className="font-medium flex items-center">
                <Maybe condition={props.loading === true}>
                    <FontAwesomeIcon icon="circle-notch" spin className="text-xl font-bold" />
                </Maybe>
                <Maybe condition={props.loading !== true}>
                    <div className="capitalize-first-letter">
                        {props.value}
                    </div>
                    <Maybe condition={props.copyToClipboard === true}>
                        <div onClick={copyValue} className="w-5 h-5 ml-2 cursor-pointer transform transition-all hover:scale-125">
                            <ImgCopy className="w-full h-full svg-fill-primary object-contain" />
                        </div>
                    </Maybe>
                </Maybe>
            </div>
        </div>
    )
}



export default Details;